import * as Sentry from '@sentry/react';
import {readAppEnv} from '~/environment';

type LogLevel = 'info' | 'log' | 'debug' | undefined | '';

const breadcrumbLevel: LogLevel = import.meta.env.VITE_SENTRY_BREADCRUMBS_LOG_LEVEL;
const exceptionLevel: LogLevel = import.meta.env.VITE_SENTRY_EXCEPTION_LOG_LEVEL;

if (import.meta.env.PROD || breadcrumbLevel || exceptionLevel) {
    initSentry();
}

function initSentry() {
    const opt: Sentry.BrowserOptions = {
        dsn: 'https://098e05d72a44402aacdd44c543a3456a@o530637.ingest.sentry.io/5807937',
        integrations: [
            Sentry.browserApiErrorsIntegration(),
            Sentry.replayIntegration({
                beforeErrorSampling(event) {
                    switch (event.level) {
                        case 'fatal':
                        case 'error':
                        case 'debug':
                            return true;
                        case 'warning':
                        case 'log':
                        case 'info':
                        default:
                            return false;
                    }
                },
            }),
            Sentry.linkedErrorsIntegration(),
        ],

        // We don't care about 401 errors as long as it handled correctly and user is notified (or at least logout)
        ignoreErrors: ['SessionExpired'],
        tracesSampleRate: 1.0,

        // if there's no APP_ENV we must assume prod
        environment: readAppEnv() ?? 'prod',
        release: `${RELEASE_VERSION}:${IMAGE_TAG}`,

        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
    };

    const levelStyles: Record<Sentry.SeverityLevel, string> = {
        fatal: 'color:red;font-weight:bold;',
        error: 'color:red;',
        warning: 'color:orange;',
        info: 'color:lightgreen;',
        debug: 'color:purple;',
        log: 'color:gray;',
    };

    if (breadcrumbLevel) {
        const log = console[breadcrumbLevel].bind(console);
        const original = opt.beforeBreadcrumb ?? (b => b);
        opt.beforeBreadcrumb = breadcrumb => {
            const {category, message, data, level = 'info'} = breadcrumb;
            const style = levelStyles[level] ?? '';
            log(`%c${category}`, style, ...[message, data].filter(Boolean));
            return original(breadcrumb);
        };
    }

    if (exceptionLevel) {
        const log = console[exceptionLevel].bind(console);
        const original = opt.beforeSend ?? (e => e);
        opt.beforeSend = (event, hint) => {
            const {message, exception, level = 'info'} = event;
            const style = levelStyles[level];
            log(`%c${message || exception?.values?.map(({value}) => value).join('\n') || level}`, style, event);
            return original(event, hint);
        };
    }

    {
        Sentry.init(opt);
    }

    addDocumentBreadcrumbListener('visibilitychange', 'ui.event', () => ({
        // these two states should be completely matching in semantics
        // but just in case I wan't to know both, can never trust iOS
        visibilityState: document.visibilityState,
        hidden: document.hidden,
    }));

    addDocumentBreadcrumbListener('fullscreenchange', 'ui.event');

    addWindowBreadcrumbListener('online', 'network');
    addWindowBreadcrumbListener('offline', 'network');

    addWindowBreadcrumbListener('pagehide', 'ui.event');
    addWindowBreadcrumbListener('pagereveal', 'ui.event');
    addWindowBreadcrumbListener('pageshow', 'ui.event');
    addWindowBreadcrumbListener('pageswap', 'ui.event');
    addWindowBreadcrumbListener('beforeunload', 'ui.event');

    addWindowBreadcrumbListener('focus', 'ui.event');
    addWindowBreadcrumbListener('blur', 'ui.event');

    addWindowBreadcrumbListener('storage', 'storage', ev => ({
        url: ev.url,
        key: ev.key,
        upd: [ev.oldValue && ev.oldValue.length, ev.newValue && ev.newValue.length],
        area: getStorageAreaName(ev),
    }));
}

function getStorageAreaName(ev: StorageEvent) {
    return new Map([
        [window.sessionStorage, 'Session storage'],
        [window.localStorage, 'Local storage'],
        [null, null],
    ]).get(ev.storageArea);
}

function addWindowBreadcrumbListener<K extends keyof WindowEventMap>(
    type: K,
    category: string,
    getData: (ev: WindowEventMap[K]) => any = () => undefined
) {
    window.addEventListener(type, ev => {
        const data = getData(ev);
        const breadcrumb: Sentry.Breadcrumb = {
            category: category,
            message: type,
            ...(data ? {data} : {}),
        };
        Sentry.addBreadcrumb(breadcrumb);
    });
}

function addDocumentBreadcrumbListener<K extends keyof DocumentEventMap>(
    type: K,
    category: string,
    getData: (ev: DocumentEventMap[K]) => any = () => undefined
) {
    document.addEventListener(type, ev => {
        const data = getData(ev);
        const breadcrumb: Sentry.Breadcrumb = {
            category: category,
            message: type,
            ...(data ? {data} : {}),
        };
        Sentry.addBreadcrumb(breadcrumb);
    });
}
